import type { IOptimusJobInputs } from '@app/components/report-optimus/components/optimus-jobs.interface.ts'
import type { ISignalSpecs } from '@app/components/optimus-configuration/optimus.interface.ts'
import { OPTIMUS_JOB_SIGNAL_STATUS } from '#frontend/components/optimus-configuration/helpers/optimusHelpers.ts'
import type {
	IOptimusDetailedJob,
	SubmitOptimusJobBody,
	DeployedParamGroup,
	IOverlappingPlanCandidate,
	OverlappingPlansBySignalId,
} from '#frontend/queries/optimus/optimus-report-request.ts'
import { isShortUuid } from '@shared-schemas/common'
import { DEFAULT_OPERATING_MODE } from '@shared-schemas/optimus/optimus-config-v2'

// Aggregated multi-signal TODs (deduped by description in the job modal) have no single saved id, so
// their time_of_day_id is a description string. The submit contract requires a short UUID or 'custom';
// emit 'custom' for anything that isn't a real saved id (the BE treats 'custom' as a Custom Period).
const normalizeTodId = (raw: unknown): string => (isShortUuid(raw) ? raw : 'custom')

type IJobSettings = IOptimusDetailedJob['settings']
type IJobSettingsSignal =
	| NonNullable<IJobSettings['signals_optimization']>[number]
	| NonNullable<IJobSettings['signals_ml']>[number]
	| NonNullable<IJobSettings['signals']>[number]
type IJobSettingsDeployedSignal = NonNullable<IJobSettings['signals_deployed']>[number]

// signals_optimization is the canonical per-signal field; fall back to legacy `signals_ml` / `signals` for historical jobs.
export const getJobSettingsSignals = (settings: IJobSettings | undefined): IJobSettingsSignal[] => {
	const optimization = settings?.signals_optimization
	if (optimization && optimization.length) return optimization
	const ml = settings?.signals_ml
	if (ml && ml.length) return ml
	return settings?.signals ?? []
}

// signals_deployed holds the current/deployed timing per signal; returned keyed by id for lookup.
export const getJobSettingsDeployedById = (
	settings: IJobSettings | undefined,
): Map<string, IJobSettingsDeployedSignal> => new Map((settings?.signals_deployed ?? []).map((d) => [d.id, d]))

// Every deployed value that ran during the job window, per signal per parameter. Absent for jobs
// submitted before this was recorded — callers fall back to split_input, which is what those jobs
// have always shown.
export const getJobSettingsOverlappingPlans = (
	settings: IJobSettings | undefined,
): OverlappingPlansBySignalId | null => settings?.overlapping_plans ?? null

// The candidates for one parameter, ordered by how much of the window each held. Empty when the job
// predates the record or the window resolved to nothing at all.
export const getOverlappingCandidates = (
	overlapping: OverlappingPlansBySignalId | null,
	signal_id: string,
	group: DeployedParamGroup,
): IOverlappingPlanCandidate[] => overlapping?.[signal_id]?.[group]?.candidates ?? []

// "07:00–14:00" — the times only. The date range and weekdays are the job's own, already shown in the
// report header, so repeating them per candidate would bury the part that differs.
export const formatOverlappingWindows = (candidate: IOverlappingPlanCandidate): string =>
	candidate.windows.map((w) => `${w.local_start_time.slice(0, 5)}–${w.local_end_time.slice(0, 5)}`).join(', ')

// What a single-value cell needs to render one parameter: the value that held most of the window, how
// many competed for it, and the copy explaining the rest. `count <= 1` means business as usual.
export interface IOverlappingSummary {
	count: number
	dominant: IOverlappingPlanCandidate | null
	omitted_count: number
	description: string | null
}

export const summarizeOverlappingPlans = (
	overlapping: OverlappingPlansBySignalId | null,
	signal_id: string,
	group: DeployedParamGroup,
): IOverlappingSummary => {
	const candidates = getOverlappingCandidates(overlapping, signal_id, group)
	const omitted_count = overlapping?.[signal_id]?.[group]?.omitted_count ?? 0
	if (candidates.length < 2) {
		return { count: candidates.length, dominant: candidates[0] ?? null, omitted_count, description: null }
	}
	const listed = candidates
		.map(
			(candidate) =>
				`${formatOverlappingWindows(candidate)} plan ${candidate.plan_numbers.join('/') || '?'} ` +
				`(${Math.round(candidate.coverage_pct)}%)`,
		)
		.join(', ')
	const tail = omitted_count > 0 ? `, and ${omitted_count} more` : ''
	return {
		count: candidates.length,
		dominant: candidates[0],
		omitted_count,
		description:
			`${candidates.length} plans ran during this window (${listed}${tail}), so the optimizer generated its own starting values.`,
	}
}

// split_constraints is a record keyed by phase ("1".."16") in the consolidated shape, but legacy jobs
// (and the in-memory editing draft) use an array. Always read it as an array of phase entries.
export const normalizeSplitConstraints = <T>(sc: T[] | Record<string, T> | null | undefined): T[] =>
	Array.isArray(sc) ? sc : Object.values(sc ?? {})

// Inverse of normalizeSplitConstraints: build the phase-keyed record the wire/persistence expect.
const splitConstraintsToRecord = <T extends { number: number | string }>(arr: T[]): Record<string, T> =>
	Object.fromEntries(arr.map((sc) => [String(sc.number), sc]))

// signals_ml and signals_optimization share the same rich per-signal shape (record-keyed
// split_constraints); only the oldest `signals` field uses the array shape. The deployed projection
// is built from this rich shape, so resolve it without falling back to `signals`.
type IJobSettingsOptimizationSignal = NonNullable<IJobSettings['signals_optimization']>[number]
const getOptimizationSignals = (settings: IJobSettings | undefined): IJobSettingsOptimizationSignal[] => {
	const optimization = settings?.signals_optimization
	if (optimization && optimization.length) return optimization
	return settings?.signals_ml ?? []
}

// A job is new-config once signals_deployed has been generated alongside signals_optimization. Legacy
// jobs persisted before the split carry only signals_ml / signals and have no signals_deployed.
export const isLegacyJobConfig = (settings: IJobSettings | undefined): boolean =>
	!(settings?.signals_deployed && settings.signals_deployed.length)

// FE mirror of the backend buildSignalsDeployed (optimus-v2/index.js): projects the optimization
// signals into the deployed (current) shape so a legacy job gains a signals_deployed on rerun.
const buildDeployedFromOptimization = (signals: IJobSettingsOptimizationSignal[]): IJobSettingsDeployedSignal[] =>
	signals.map((signal) => {
		const splits: IJobSettingsDeployedSignal['splits'] = {}
		for (const sc of normalizeSplitConstraints(signal.split_constraints)) {
			splits[String(sc.number)] = {
				red: sc.red ?? null,
				ring: sc.ring ?? null,
				number: sc.number,
				yellow: sc.yellow ?? null,
				barrier: sc.barrier ?? null,
				is_enabled: sc.is_enabled ?? false,
				dummy_phase: sc.dummy_phase ?? false,
				current_split: sc.current_split ?? null,
				vehicle_extension: sc.vehicle_extension ?? 3,
				minimum_green: sc.minimum_green ?? null,
				is_coordinated: sc.is_coordinated ?? null,
				sequence_index: sc.sequence_index ?? null,
				pedestrian_phase: sc.pedestrian_phase ?? false,
				edge_connection_mapping: sc.edge_connection_mapping ?? [],
			}
		}

		const offset = signal.offset_constraints
		const sequence = offset?.sequence ?? null
		const current_offset = offset?.current_offset ?? null
		const offset_reference_point = offset?.offset_reference_point ?? null
		const cycle = signal.cycle_length_constraints?.current_cycle_length ?? null

		const sequence_phases = (sequence?.rings ?? [])
			.flat()
			.filter((slot) => slot.type === 'phase' && slot.phase !== 0)
		const has_missing_enabled_phase = Object.values(splits).some(
			(p) => p.is_enabled && (p.red == null || p.yellow == null || p.current_split == null),
		)
		const is_missing_params =
			has_missing_enabled_phase ||
			sequence_phases.length === 0 ||
			current_offset == null ||
			offset_reference_point == null ||
			cycle == null

		return {
			id: signal.id,
			name: signal.name ?? null,
			is_configured: signal.is_configured ?? false,
			intersection_ids: signal.intersection_ids ?? [],
			splits,
			sequence,
			current_offset,
			force_off_mode: offset?.force_off_mode ?? null,
			offset_reference_point,
			cycle,
			mode: signal.operating_mode_constraints?.current_mode ?? DEFAULT_OPERATING_MODE,
			is_missing_params,
		}
	})

// Detection + normalization layer for reruns: a legacy job (signals_ml only) is promoted to the
// canonical shape — signals_optimization plus a derived signals_deployed — so the rerun behaves like
// a new-config job. New-config jobs pass through unchanged.
export const normalizeJobSettingsConfig = (settings: IJobSettings): IJobSettings => {
	if (!isLegacyJobConfig(settings)) return settings
	const signals_optimization = getOptimizationSignals(settings)
	return {
		...settings,
		signals_optimization,
		signals_deployed: buildDeployedFromOptimization(signals_optimization),
	}
}

export const constructSubmitJobBody = (signal_specs: ISignalSpecs[], new_job_settings: IOptimusJobInputs) => {
	const {
		signals_in_optimization,

		customer_id,
		network_id,
		tod,
		reference_time_period,
		timing_parameters,
		cycle_length_constraints,
		objectives,
		signals_optimization,
		job_name,
	} = new_job_settings

	const formatted_signals = signals_optimization.map((signal) => {
		const sig_spec = signal_specs.find((s) => s.id === signal.id)

		const has_null_cycle = signal.cycle_length_constraints.current_cycle_length == null
		const has_null_offset = signal.offset_constraints.current_offset == null
		const has_null_split = signal.split_constraints.some(
			(sc) =>
				sc.is_enabled &&
				(sc.current_split == null ||
					sc.min_pedestrian_walk_time == null ||
					sc.min_pedestrian_clearance == null ||
					sc.yellow == null ||
					sc.red == null),
		)
		const is_complete = sig_spec?.optimus_job_signal_status === OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE

		const is_complete_but_has_flowlabs_supplemental_params =
			is_complete && (has_null_cycle || has_null_offset || has_null_split)

		const valid_statuses: string[] = [OPTIMUS_JOB_SIGNAL_STATUS.MET_MINIMUM, OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE]

		const status_is_valid = valid_statuses.includes(sig_spec?.optimus_job_signal_status || '')
		return {
			...signal,
			split_constraints: splitConstraintsToRecord(signal.split_constraints),
			is_configured: signals_in_optimization.includes(signal.id) && status_is_valid,
			has_flowlabs_supplemental_params:
				sig_spec?.optimus_job_signal_status !== OPTIMUS_JOB_SIGNAL_STATUS.COMPLETE ||
				is_complete_but_has_flowlabs_supplemental_params,
		}
	})
	// BEGIN TEMP HACK - Insert signals not participating in Optimus for legacy backend
	// TODO1: Check if removing this is okay before committing changes
	// .concat(
	// 	signal_specs
	// 		.map((s) => {
	// 			if (signals_in_optimization.includes(s.id)) return
	// 			return {
	// 				id: s.id,
	// 				enable_optimization: false,
	// 				is_configured: false,
	// 			}
	// 		})
	// 		.filter((x) => x),
	// )
	// END TEMP HACK - Insert signals not participating in Optimus for legacy backend

	const backwards_compatible_tod: SubmitOptimusJobBody['tod'] = {
		id: normalizeTodId(tod.time_of_day_id),
		name: tod.name,
		mode: tod.time_of_day_data.mode,
		days_boolean: tod.time_of_day_data.days,
		local_start_time: tod.time_of_day_data.local_start_time,
		local_end_time: tod.time_of_day_data.local_end_time,
		days: [
			tod.time_of_day_data.days.monday ? 'Monday' : null,
			tod.time_of_day_data.days.tuesday ? 'Tuesday' : null,
			tod.time_of_day_data.days.wednesday ? 'Wednesday' : null,
			tod.time_of_day_data.days.thursday ? 'Thursday' : null,
			tod.time_of_day_data.days.friday ? 'Friday' : null,
			tod.time_of_day_data.days.saturday ? 'Saturday' : null,
			tod.time_of_day_data.days.sunday ? 'Sunday' : null,
		].filter((day) => day !== null),
		months: tod.time_of_day_data.months,
		days_of_month: tod.time_of_day_data.days_of_month,
	}

	const job_body: SubmitOptimusJobBody = {
		customer_id,
		network_id,
		tod: backwards_compatible_tod,
		reference_time_period,
		timing_parameters,
		cycle_length_constraints,
		objectives,
		signals_optimization: formatted_signals,
		job_name: job_name || '',
		is_visible: new_job_settings.is_visible ?? false,
		is_remote: new_job_settings.is_remote ?? false,
	}

	return job_body
}

export const convertDetailedJobToSubmitBody = (job: IOptimusDetailedJob): SubmitOptimusJobBody => {
	const { settings, job_id, parent_id, job_name } = job

	const tod_days_boolean = settings.tod.days_boolean
	const tod: SubmitOptimusJobBody['tod'] = {
		id: normalizeTodId(settings.tod.id),
		name: settings.tod.name,
		mode: settings.tod.mode ?? 'coordinated',
		days_boolean: {
			monday: tod_days_boolean.has_monday,
			tuesday: tod_days_boolean.has_tuesday,
			wednesday: tod_days_boolean.has_wednesday,
			thursday: tod_days_boolean.has_thursday,
			friday: tod_days_boolean.has_friday,
			saturday: tod_days_boolean.has_saturday,
			sunday: tod_days_boolean.has_sunday,
		},
		local_start_time: settings.tod.local_start_time,
		local_end_time: settings.tod.local_end_time,
		days: settings.tod.days,
		months: settings.tod.months,
		days_of_month: settings.tod.days_of_month,
	}

	// Persisted settings store the weight normalized ((raw + 2) / 4 → 0.25/0.5/0.75); jobs from before
	// that normalization stored the raw -1/0/1, which round-trips unchanged. The submit API expects raw.
	const denormalizeBiasWeight = (weight: number | null | undefined): number => {
		if (weight == null) return 0
		if (weight === -1 || weight === 0 || weight === 1) return weight
		return Math.round(weight * 4 - 2)
	}

	const persisted_bias = settings.objectives.directional_bias
	const bias_weight = denormalizeBiasWeight(persisted_bias.weight)
	const objectives: SubmitOptimusJobBody['objectives'] = {
		directional_bias: {
			weight: bias_weight,
			start_signal_id: persisted_bias.start_signal_id,
			// Fallback-balanced jobs persist a route_id even though no bias was chosen — only carry
			// it (and the direction) forward when a bias is actually requested.
			route_id: bias_weight === 0 ? null : (persisted_bias.route_id ?? null),
			direction: bias_weight === 0 ? null : (persisted_bias.direction ?? null),
		},
		target_speed_mph: settings.objectives.target_speed_mph,
		safety: settings.objectives.safety,
		bandwidth: settings.objectives.bandwidth,
		side_street_control_delay_tolerance: settings.objectives.side_street_control_delay_tolerance,
	}

	const signals_optimization: SubmitOptimusJobBody['signals_optimization'] = getJobSettingsSignals(settings).map(
		(signal) => ({
			...signal,
			id: signal.id,
			is_configured: signal.is_configured ?? false,
			has_flowlabs_supplemental_params: signal.has_flowlabs_supplemental_params ?? false,
			enable_optimization: signal.enable_optimization,
			split_constraints: splitConstraintsToRecord(normalizeSplitConstraints(signal.split_constraints)),
		}),
	)

	return {
		customer_id: settings.customer_id,
		network_id: settings.network_id,
		tod,
		reference_time_period: settings.reference_time_period,
		timing_parameters: settings.timing_parameters,
		cycle_length_constraints: {
			...settings.cycle_length_constraints,
			selected_cycle_lengths: [],
		},
		objectives,
		signals_optimization,
		job_name: job_name ?? '',
		parent_id: parent_id ?? job_id,
		is_visible: job.is_visible ?? false,
		is_remote: job.is_remote ?? false,
	}
}
